import type { StatusId } from '@shefira/shared';
import type Phaser from 'phaser';

/** Palette colours (data/assets/palette.gpl). */
const WHITE = 0xf8f2ea;
const GOLD_TIP = 0xfff1a8;
const GOLD = 0xffc82e;
const ORANGE = 0xf07a1c;
const EMBER = 0xc23a12;
const BLOOD = 0xbd3644;
const BLOOD_DARK = 0x532622;
const FROST_LIGHT = 0x66afd7;
const FROST = 0x498ace;
const FROST_DARK = 0x31679d;
const METAL_LIGHT = 0xd6d6e0;
const METAL = 0xa9a9b8;
const RIM = 0x12121c;

/** Where a character's marks go, in the view's own pixels (feet at 0, 0). */
export interface StatusGeometry {
  /** The top row of the body's drawn pixels, negative (above the feet). */
  headY: () => number;
  /** Right after the name plate, where the taunt's « ! » stands. */
  plateEnd: () => { x: number; y: number };
}

/**
 * The timed states of ADR 0048 drawn on the character that carries them:
 * stars over a stunned head, frost around the slowed, drops off the
 * bleeding, a red « ! » after a taunted creature's name, a steel glint
 * round the guarded and a red flicker round the enraged. One graphics in
 * the view's container, redrawn each frame in whole pixels (CLAUDE.md
 * rule 6) while anything is on, gone with the view. Each wears off on the
 * scene's clock after the `durationMs` the server sent: no end message.
 */
export class StatusMarks {
  readonly graphics: Phaser.GameObjects.Graphics;
  /** Scene time at which each status wears off. */
  private readonly until = new Map<StatusId, number>();
  private listening = false;
  private hidden = false;

  private readonly redraw = (): void => {
    const now = this.scene.time.now;
    for (const [status, end] of this.until) if (end <= now) this.until.delete(status);
    this.graphics.clear();
    if (this.until.size === 0) {
      this.stopListening();
      return;
    }
    if (this.hidden) return;
    for (const status of this.until.keys()) this.draw(status, now);
  };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly geometry: StatusGeometry,
  ) {
    this.graphics = scene.add.graphics();
  }

  set(status: StatusId, durationMs: number): void {
    if (durationMs <= 0) this.until.delete(status);
    else this.until.set(status, this.scene.time.now + durationMs);
    if (this.until.size > 0 && !this.listening) {
      this.listening = true;
      this.scene.events.on('update', this.redraw);
    }
    this.redraw();
  }

  /** Every state off at once: a fall ends them all. */
  clear(): void {
    this.until.clear();
    this.redraw();
  }

  setHidden(hidden: boolean): void {
    this.hidden = hidden;
    this.redraw();
  }

  destroy(): void {
    this.stopListening();
    this.graphics.destroy();
  }

  private stopListening(): void {
    if (!this.listening) return;
    this.listening = false;
    this.scene.events.off('update', this.redraw);
  }

  private dot(x: number, y: number, color: number, width = 1, height = 1): void {
    this.graphics.fillStyle(color, 1);
    this.graphics.fillRect(Math.round(x), Math.round(y), width, height);
  }

  private draw(status: StatusId, now: number): void {
    switch (status) {
      case 'stunned':
        this.stars(now);
        return;
      case 'slowed':
        this.frost(now);
        return;
      case 'bleeding':
        this.drops(now);
        return;
      case 'taunted':
        this.shout(now);
        return;
      case 'guarded':
        this.guard(now);
        return;
      case 'enraged':
        this.rage(now);
        return;
    }
  }

  /** Three little stars going round over the head, the far ones dimmer. */
  private stars(now: number): void {
    const cy = this.geometry.headY() - 2;
    for (let star = 0; star < 3; star += 1) {
      const angle = now / 260 + (star / 3) * Math.PI * 2;
      const x = Math.cos(angle) * 7;
      const y = cy + Math.sin(angle) * 2;
      const near = Math.sin(angle) >= 0;
      const arm = near ? GOLD : ORANGE;
      this.dot(x - 1, y, arm);
      this.dot(x + 1, y, arm);
      this.dot(x, y - 1, arm);
      this.dot(x, y + 1, arm);
      this.dot(x, y, near ? GOLD_TIP : GOLD);
    }
  }

  /** Frost: a pale ring on the ground and flakes drifting down round the body. */
  private frost(now: number): void {
    const pulse = Math.floor(now / 200) % 2 === 0;
    for (let step = 0; step < 24; step += 1) {
      const angle = (step / 24) * Math.PI * 2;
      if (step % 2 === (pulse ? 0 : 1)) continue;
      this.dot(Math.cos(angle) * 7, -1 + Math.sin(angle) * 2, step % 4 < 2 ? FROST : FROST_DARK);
    }
    const top = this.geometry.headY();
    for (let flake = 0; flake < 5; flake += 1) {
      const phase = (now / 1400 + flake / 5) % 1;
      const side = flake % 2 === 0 ? -1 : 1;
      const x = side * (5 + ((flake * 3) % 4)) + Math.round(Math.sin(phase * 6 + flake) * 1);
      const y = top + 2 + phase * (-top - 3);
      this.dot(x, y, phase < 0.6 ? FROST_LIGHT : FROST);
    }
  }

  /** Drops falling off the wound to the ground, one after the other. */
  private drops(now: number): void {
    const top = this.geometry.headY();
    const from = Math.round(top * 0.55);
    for (let drop = 0; drop < 3; drop += 1) {
      const t = now / 700 + drop / 3;
      const phase = t % 1;
      const cycle = Math.floor(t);
      // A new place each fall, from a small hash of the drop and its cycle.
      const x = ((((cycle * 7 + drop * 5) % 9) + 9) % 9) - 4;
      const y = from + phase * -from;
      if (phase < 0.85) {
        this.dot(x, y, BLOOD, 1, 2);
        this.dot(x, y - 1, BLOOD_DARK);
      } else {
        // A splash on the ground as it lands.
        this.dot(x - 1, -1, BLOOD_DARK);
        this.dot(x + 1, -1, BLOOD_DARK);
        this.dot(x, -1, BLOOD);
      }
    }
  }

  /** A red « ! » after the name, blinking: this one is after the warrior now. */
  private shout(now: number): void {
    const { x, y } = this.geometry.plateEnd();
    const bob = Math.floor(now / 250) % 2;
    const top = y - bob;
    this.graphics.fillStyle(RIM, 1);
    this.graphics.fillRect(x - 1, top - 1, 3, 5);
    this.graphics.fillRect(x - 1, top + 3, 3, 3);
    this.dot(x, top, BLOOD, 1, 3);
    this.dot(x, top + 4, BLOOD);
  }

  /** A steel glint round the body, shimmering: the shield is up. */
  private guard(now: number): void {
    const top = this.geometry.headY();
    const cy = Math.round(top / 2);
    const ry = Math.round(-top / 2) + 2;
    const rx = 10;
    const shimmer = (now / 90) % 32;
    for (let step = 0; step < 32; step += 1) {
      const angle = (step / 32) * Math.PI * 2;
      const distance = Math.abs(step - shimmer);
      const bright = distance < 2 || distance > 30;
      if (!bright && step % 3 !== 0) continue;
      this.dot(
        Math.cos(angle) * rx,
        cy + Math.sin(angle) * ry,
        bright ? WHITE : step % 2 === 0 ? METAL_LIGHT : METAL,
      );
    }
  }

  /** Red flicker all round the body and embers rising off it. */
  private rage(now: number): void {
    const top = this.geometry.headY();
    const cy = Math.round(top / 2);
    const ry = Math.round(-top / 2) + 1;
    const frame = Math.floor(now / 80);
    for (let step = 0; step < 20; step += 1) {
      // Every frame a different half of the points: the aura flickers.
      if ((step * 13 + frame) % 3 === 0) continue;
      const angle = (step / 20) * Math.PI * 2;
      const color = (step + frame) % 4 === 0 ? ORANGE : (step + frame) % 2 === 0 ? EMBER : BLOOD;
      this.dot(Math.cos(angle) * 9, cy + Math.sin(angle) * ry, color);
    }
    for (let ember = 0; ember < 3; ember += 1) {
      const phase = (now / 900 + ember / 3) % 1;
      const x = (ember - 1) * 5;
      this.dot(x, top * 0.3 + phase * top, phase < 0.5 ? ORANGE : EMBER);
    }
  }
}

/** The first row with a drawn pixel in a frame, cached by frame: where the head begins. */
const contentTops = new Map<string, number>();

export function contentTop(scene: Phaser.Scene, atlasKey: string, frameName: string): number {
  const key = `${atlasKey}:${frameName}`;
  const known = contentTops.get(key);
  if (known !== undefined) return known;
  let top = 0;
  if (scene.textures.exists(atlasKey) && scene.textures.get(atlasKey).has(frameName)) {
    const frame = scene.textures.getFrame(atlasKey, frameName);
    search: for (let y = 0; y < frame.height; y += 1) {
      for (let x = 0; x < frame.width; x += 1) {
        const pixel = scene.textures.getPixel(x, y, atlasKey, frameName);
        if (pixel !== null && pixel.alpha > 0) {
          top = y;
          break search;
        }
      }
    }
  }
  contentTops.set(key, top);
  return top;
}
